import "./site/error-beacon";
import { Pulse, PulseEvent } from "./games/pulse/pulse";
import { runCalibrationWizard, loadTapOffsetMs, saveTapOffsetMs } from "./games/pulse/calibration";
import { AudioBed } from "./game/audio";
import { recordRun, showXpToast } from "./site/profile";
import { getDailySeed, markDailyComplete, isDailyRun } from "./site/daily";
import { getActiveChallengeCode, acceptChallenge, createChallenge, fetchChallenge } from "./site/challenge";
import "./ui/styles.css";

const PB_KEY = "aether.pulse_pb";
const HANDLE_KEY = "aether.handle";

interface StartResp { run_id: string; seed: number; token: string; game: string }

async function startPulseRun(): Promise<StartResp | null> {
  try {
    const res = await fetch("/api/runs/pulse", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({}),
    });
    if (!res.ok) return null;
    return (await res.json()) as StartResp;
  } catch { return null; }
}

async function finishPulseRun(runId: string, handle: string, events: PulseEvent[]) {
  try {
    const res = await fetch(`/api/runs/pulse/${runId}`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ handle, events }),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch { return null; }
}

const canvas = document.getElementById("stage") as HTMLCanvasElement;
const hud = document.querySelector(".hud") as HTMLElement;
const hudBeat = document.getElementById("hud-beat")!;
const hudLast = document.getElementById("hud-last")!;
const hudScore = document.getElementById("hud-score")!;
const sectionChip = document.getElementById("section-chip") as HTMLElement | null;
const actBanner = document.getElementById("act-banner") as HTMLElement | null;
const msgTitle = document.getElementById("msg-title")!;
const msgBody = document.getElementById("msg-body")!;
const btnStart = document.getElementById("btn-start") as HTMLButtonElement;
const btnCalibrate = document.getElementById("btn-calibrate") as HTMLButtonElement | null;
const calOffsetReadout = document.getElementById("cal-offset-readout") as HTMLElement | null;
const calAdvancedToggle = document.getElementById("cal-advanced-toggle") as HTMLInputElement | null;
const calAdvancedPanel = document.getElementById("cal-advanced-panel") as HTMLElement | null;
const calOffsetSlider = document.getElementById("cal-offset-slider") as HTMLInputElement | null;
const calOffsetValue = document.getElementById("cal-offset-value") as HTMLElement | null;
const submitForm = document.getElementById("submit-form") as HTMLFormElement;
const handleInputOverlay = document.getElementById("handle-input-overlay") as HTMLInputElement;

let bannerTimer = 0;
function showBanner(title: string, sub: string) {
  if (!actBanner) return;
  actBanner.innerHTML = `<div class="ms-title">${title}</div><div class="ms-sub">${sub}</div>`;
  actBanner.classList.add("visible");
  window.clearTimeout(bannerTimer);
  bannerTimer = window.setTimeout(() => actBanner.classList.remove("visible"), 2400);
}

let game: Pulse | null = null;
let activeRunId: string | null = null;
let activeSeed: number | null = null;
let pendingEvents: PulseEvent[] | null = null;
let tapOffsetMs = loadTapOffsetMs();
// v5(c): shared AudioBed instance for the calibration wizard so the
// arm()-on-gesture unlock carries over into the main run that follows.
const calAudio = new AudioBed();

function renderCalReadout() {
  if (calOffsetReadout) {
    calOffsetReadout.textContent = tapOffsetMs === 0
      ? "uncalibrated"
      : `${tapOffsetMs > 0 ? "+" : ""}${tapOffsetMs} ms`;
  }
  if (calOffsetSlider) calOffsetSlider.value = String(tapOffsetMs);
  if (calOffsetValue) {
    calOffsetValue.textContent = `${tapOffsetMs > 0 ? "+" : ""}${tapOffsetMs} ms`;
  }
}
renderCalReadout();

if (btnCalibrate) {
  btnCalibrate.addEventListener("click", async () => {
    btnCalibrate.disabled = true;
    const prev = btnCalibrate.textContent;
    btnCalibrate.textContent = "Calibrating…";
    const res = await runCalibrationWizard(calAudio);
    btnCalibrate.disabled = false;
    btnCalibrate.textContent = prev ?? "Calibrate (3 taps)";
    if (res) {
      tapOffsetMs = res.offsetMs;
      renderCalReadout();
    }
  });
}

if (calAdvancedToggle && calAdvancedPanel) {
  calAdvancedToggle.addEventListener("change", () => {
    calAdvancedPanel.hidden = !calAdvancedToggle.checked;
  });
}

if (calOffsetSlider) {
  calOffsetSlider.addEventListener("input", () => {
    const n = Number(calOffsetSlider.value);
    if (Number.isFinite(n)) {
      tapOffsetMs = Math.max(-150, Math.min(150, Math.round(n)));
      saveTapOffsetMs(tapOffsetMs);
      renderCalReadout();
    }
  });
}

const challengeCode = getActiveChallengeCode();
let challengeInfo: Awaited<ReturnType<typeof fetchChallenge>> = null;
if (challengeCode) {
  fetchChallenge(challengeCode).then((v) => {
    challengeInfo = v;
    if (v && !v.expired && !v.accepter) {
      msgBody.textContent = `${v.challenger.handle} challenges you — beat ${v.challenger.score.toLocaleString()}.`;
    }
  });
}

async function startRun() {
  if (game) game.dispose();
  const dseed = getDailySeed();
  const runStart = dseed === null ? await startPulseRun() : null;
  activeRunId = runStart?.run_id ?? null;
  const seed = dseed ?? runStart?.seed ?? Math.floor(Math.random() * 0x7fffffff);
  activeSeed = seed;

  game = new Pulse(canvas, {
    onHud: (idx, total, lastBucket, scoreEstimate, _section, comboStreak) => {
      hudBeat.textContent = `${idx} / ${total}`;
      hudLast.textContent = `${lastBucket} · ${comboStreak}x`;
      hudScore.textContent = scoreEstimate.toLocaleString();
    },
    onSection: (section) => {
      if (sectionChip) {
        sectionChip.textContent = section.toUpperCase();
        sectionChip.className = `section-chip section-${section}`;
      }
      if (section !== "verse") {
        const sub = section === "drop" ? "x1.4 score · 20–45s" : "x1.15 score · 45–60s";
        showBanner(`${section.toUpperCase()}`, sub);
      }
    },
    onFinisher: (level) => {
      const bonuses = [300, 700, 1500];
      showBanner(`COMBO FINISHER ${level}`, `${[10, 20, 30][level - 1]}× streak · +${bonuses[level - 1]} pts`);
    },
    onGameOver: async ({ events, scoreEstimate, bestCombo, bucketsByName, chordsHit, finishersEarned }) => {
      hud.dataset.state = "over";
      msgTitle.textContent = "Run complete";
      const summary = `${bucketsByName.perfect}✦ · ${bucketsByName.great}● · ${bucketsByName.good}· · ${bucketsByName.miss}✕`;
      const chordsTag = chordsHit > 0 ? ` · ${chordsHit}🎵 chords` : "";
      const finishTag = finishersEarned > 0 ? ` · ${finishersEarned}⚡ finishers` : "";
      msgBody.textContent = `${scoreEstimate.toLocaleString()} pts · ${summary} · best combo ${bestCombo}${chordsTag}${finishTag}`;
      btnStart.textContent = "Try again";
      try {
        const pb = parseInt(localStorage.getItem(PB_KEY) || "0", 10) || 0;
        if (scoreEstimate > pb) localStorage.setItem(PB_KEY, String(scoreEstimate));
      } catch { /* private */ }
      const xp = recordRun({ game: "pulse", score: scoreEstimate });
      showXpToast(xp.earnedXp, xp.totalXp, xp.rank);
      if (isDailyRun()) markDailyComplete("pulse", scoreEstimate);

      if (!activeRunId) {
        renderChallengeFooter(scoreEstimate, null);
        return;
      }
      const handle = localStorage.getItem(HANDLE_KEY) ?? "";
      if (handle) {
        const res = await finishPulseRun(activeRunId, handle, events);
        if (res) msgBody.textContent += ` · ranked #${res.rank} of ${res.total}`;
        await handleChallengeOnGameOver(handle, scoreEstimate, activeRunId);
        renderChallengeFooter(scoreEstimate, handle);
      } else {
        pendingEvents = events;
        submitForm.hidden = false;
      }
    },
  }, seed, { tapOffsetMs });
  game.armAudio();
  try {
    const raw = localStorage.getItem("aether.settings");
    if (raw) {
      const s = JSON.parse(raw);
      if (typeof s.audioVolume === "number") game.setVolume(s.audioVolume);
      if (typeof s.muted === "boolean") game.setMuted(s.muted);
    }
  } catch { /* defaults */ }
  game.start();
  hud.dataset.state = "running";
  msgTitle.textContent = "Pulse";
  msgBody.textContent = "";
}

btnStart.addEventListener("click", startRun);

submitForm.addEventListener("submit", async (e) => {
  e.preventDefault();
  if (!activeRunId || !pendingEvents) return;
  const value = handleInputOverlay.value.trim();
  if (!/^[A-Za-z0-9_-]{1,16}$/.test(value)) return;
  localStorage.setItem(HANDLE_KEY, value);
  const res = await finishPulseRun(activeRunId, value, pendingEvents);
  pendingEvents = null;
  submitForm.hidden = true;
  if (res) msgBody.textContent += ` · ranked #${res.rank} of ${res.total}`;
});

window.addEventListener("keydown", (e) => {
  if (e.target instanceof HTMLInputElement) return;
  if (e.code === "Enter" && hud.dataset.state !== "running") startRun();
});

async function handleChallengeOnGameOver(handle: string, score: number, runId: string) {
  if (!challengeCode || !challengeInfo || challengeInfo.expired) return;
  if (challengeInfo.accepter) return;
  const result = await acceptChallenge(challengeCode, handle, score, runId);
  if (!result) return;
  const verdict =
    result.winner === handle ? `🏆 YOU WIN — outscored ${result.challenger.handle} by ${(score - result.challenger.score).toLocaleString()} pts`
    : result.winner === "tie" ? `It's a tie at ${score.toLocaleString()} pts`
    : `${result.challenger.handle} wins — ${(result.challenger.score - score).toLocaleString()} pts ahead`;
  msgBody.textContent += ` · ${verdict}`;
}

function renderChallengeFooter(score: number, handle: string | null) {
  // Note: this footer renders for BOTH challengers and accepters. Accepters
  // who just finished an incoming challenge see a "Challenge them back"
  // button to rematch with a fresh code (the new challenge uses the
  // current run's seed, not the just-played challenge's seed — they're
  // sending their best back, not the same fight). Challengers see the
  // standard "Challenge a friend" CTA.
  const isRematch = !!challengeCode;
  let footer = document.getElementById("chal-footer");
  if (!footer) {
    footer = document.createElement("div");
    footer.id = "chal-footer";
    footer.className = "chal-footer";
    document.querySelector(".screen-msg")?.appendChild(footer);
  }
  if (!handle || !activeSeed) {
    footer.innerHTML = `<small>Set a handle to ${isRematch ? "rematch" : "share a head-to-head challenge with this run's seed"}.</small>`;
    return;
  }
  const ctaLabel = isRematch ? "Challenge them back →" : "Challenge a friend →";
  footer.innerHTML = `<button type="button" class="btn ghost chal-btn" id="btn-challenge">${ctaLabel}</button><span class="chal-status" id="chal-status"></span>`;
  const btn = document.getElementById("btn-challenge") as HTMLButtonElement;
  const status = document.getElementById("chal-status") as HTMLElement;
  btn.addEventListener("click", async () => {
    btn.disabled = true;
    btn.textContent = "Creating…";
    const res = await createChallenge({ game: "pulse", seed: activeSeed!, handle, score, runId: activeRunId ?? undefined });
    if (!res) { btn.disabled = false; btn.textContent = "Try again"; status.textContent = "Failed."; return; }
    const link = `${location.origin}/challenge?code=${res.code}`;
    btn.textContent = "Copied!";
    try { await navigator.clipboard?.writeText(link); } catch { /* ignore */ }
    status.innerHTML = `Code <code>${res.code}</code> · <a href="${link}" target="_blank">${link}</a>`;
  });
}
